/**
 * FileLoader - Handles parsing molecular file formats
 *
 * Supports: SDF, MOL, MOL2, PDB, XYZ, HIN, CRD
 * Extended from simple-viewer with PF MolView format support
 */

import type { MoleculeEntry, MoleculeFormat } from './types';

/**
 * Determine file format from extension
 */
export function getFormatFromFilename(filename: string): MoleculeFormat | null {
  const ext = filename.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'sdf':
    case 'sd':
      return 'sdf';
    case 'mol':
      return 'mol';
    case 'mol2':
      return 'mol2';
    case 'pdb':
    case 'ent':
      return 'pdb';
    case 'xyz':
      return 'xyz';
    case 'hin':
      return 'hin';
    case 'crd':
      return 'crd';
    default:
      return null;
  }
}

/**
 * Parse SDF file (can contain multiple molecules)
 */
function parseSDFMultiple(data: string, sourceFilename: string): Omit<MoleculeEntry, 'id'>[] {
  const molecules: Omit<MoleculeEntry, 'id'>[] = [];

  // Split by $$$$ delimiter
  const blocks = data.split('$$$$').filter(block => block.trim().length > 0);

  blocks.forEach((block, index) => {
    const lines = block.trim().split('\n');

    // First line is typically the molecule name
    let name = lines[0]?.trim() || '';
    if (!name) {
      name = `${sourceFilename.replace(/\.(sdf|sd)$/i, '')}_${index + 1}`;
    }

    // Extract properties from data block
    const properties: Record<string, string> = {};
    let currentPropName = '';
    let inProperty = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Property header: > <PROPERTY_NAME>
      if (line.startsWith('> <') || line.startsWith('>  <')) {
        const match = line.match(/>\s*<([^>]+)>/);
        if (match) {
          currentPropName = match[1];
          inProperty = true;
        }
      } else if (inProperty && line.trim() && !line.startsWith('>')) {
        // Property value (first non-empty line after header)
        properties[currentPropName] = line.trim();
        inProperty = false;
      } else if (line.trim() === '' && inProperty) {
        inProperty = false;
      }
    }

    // Extract atom count from counts line (line 4, 0-indexed line 3)
    let atomCount: number | undefined;
    let bondCount: number | undefined;
    if (lines.length > 3) {
      const countsLine = lines[3];
      const counts = countsLine.trim().split(/\s+/);
      if (counts.length >= 2) {
        atomCount = parseInt(counts[0], 10);
        bondCount = parseInt(counts[1], 10);
      }
    }

    molecules.push({
      name,
      data: block.trim() + '\n$$$$',
      format: 'sdf',
      properties,
      atomCount: isNaN(atomCount!) ? undefined : atomCount,
      bondCount: isNaN(bondCount!) ? undefined : bondCount,
    });
  });

  return molecules;
}

/**
 * Parse MOL file (single molecule)
 */
function parseMOL(data: string, sourceFilename: string): Omit<MoleculeEntry, 'id'>[] {
  const lines = data.trim().split('\n');
  const name = lines[0]?.trim() || sourceFilename.replace(/\.mol$/i, '');

  let atomCount: number | undefined;
  let bondCount: number | undefined;
  if (lines.length > 3) {
    const countsLine = lines[3];
    const counts = countsLine.trim().split(/\s+/);
    if (counts.length >= 2) {
      atomCount = parseInt(counts[0], 10);
      bondCount = parseInt(counts[1], 10);
    }
  }

  return [{
    name,
    data,
    format: 'mol',
    properties: {},
    atomCount: isNaN(atomCount!) ? undefined : atomCount,
    bondCount: isNaN(bondCount!) ? undefined : bondCount,
  }];
}

/**
 * Parse MOL2 file (can contain multiple molecules)
 */
function parseMOL2Multiple(data: string, sourceFilename: string): Omit<MoleculeEntry, 'id'>[] {
  const molecules: Omit<MoleculeEntry, 'id'>[] = [];

  // Split by @<TRIPOS>MOLECULE
  const blocks = data.split('@<TRIPOS>MOLECULE').filter(block => block.trim().length > 0);

  blocks.forEach((block, index) => {
    const lines = block.trim().split('\n');

    // First line after MOLECULE is the name
    const name = lines[0]?.trim() || `${sourceFilename.replace(/\.mol2$/i, '')}_${index + 1}`;

    // Second line has atom/bond counts
    let atomCount: number | undefined;
    let bondCount: number | undefined;
    if (lines.length > 1) {
      const counts = lines[1].trim().split(/\s+/);
      if (counts.length >= 2) {
        atomCount = parseInt(counts[0], 10);
        bondCount = parseInt(counts[1], 10);
      }
    }

    molecules.push({
      name,
      data: '@<TRIPOS>MOLECULE\n' + block.trim(),
      format: 'mol2',
      properties: {},
      atomCount: isNaN(atomCount!) ? undefined : atomCount,
      bondCount: isNaN(bondCount!) ? undefined : bondCount,
    });
  });

  return molecules;
}

/**
 * Parse PDB file (single structure)
 */
function parsePDB(data: string, sourceFilename: string): Omit<MoleculeEntry, 'id'>[] {
  const lines = data.split('\n');
  let name = sourceFilename.replace(/\.(pdb|ent)$/i, '');

  // Try to get name from COMPND or TITLE record
  for (const line of lines) {
    if (line.startsWith('TITLE') || line.startsWith('COMPND')) {
      const titleText = line.substring(10).trim();
      if (titleText) {
        name = titleText;
        break;
      }
    }
  }

  // Count atoms
  const atomCount = lines.filter(line =>
    line.startsWith('ATOM') || line.startsWith('HETATM')
  ).length;

  // Extract properties from HEADER, AUTHOR, etc.
  const properties: Record<string, string> = {};
  for (const line of lines) {
    if (line.startsWith('HEADER')) {
      properties['Header'] = line.substring(10, 50).trim();
    } else if (line.startsWith('EXPDTA')) {
      properties['Method'] = line.substring(10).trim();
    } else if (line.startsWith('RESOLUTION')) {
      properties['Resolution'] = line.substring(10).trim();
    }
  }

  return [{
    name,
    data,
    format: 'pdb',
    properties,
    atomCount,
  }];
}

/**
 * Parse XYZ file (single structure)
 */
function parseXYZ(data: string, sourceFilename: string): Omit<MoleculeEntry, 'id'>[] {
  const lines = data.trim().split('\n');
  const atomCount = parseInt(lines[0]?.trim() || '0', 10);
  const name = lines[1]?.trim() || sourceFilename.replace(/\.xyz$/i, '');

  return [{
    name,
    data,
    format: 'xyz',
    properties: {},
    atomCount: isNaN(atomCount) ? undefined : atomCount,
  }];
}

/**
 * Parse HIN file (HyperChem format - single structure)
 * Basic parser for HyperChem HIN format
 */
function parseHIN(data: string, sourceFilename: string): Omit<MoleculeEntry, 'id'>[] {
  const lines = data.trim().split('\n');
  let name = sourceFilename.replace(/\.hin$/i, '');
  let atomCount = 0;
  let bondCount = 0;

  const properties: Record<string, string> = {};

  for (const line of lines) {
    const parts = line.trim().split(/\s+/);

    if (parts[0] === 'mol') {
      // mol <number> <name>
      if (parts.length >= 3) {
        name = parts.slice(2).join(' ');
      }
    } else if (parts[0] === 'atom') {
      atomCount++;
      // Count bonds from the atom line (last part is number of bonds followed by bond info)
      // atom <num> <name> <element> <type> <flags> <charge> <x> <y> <z> <nbonds> [bond info...]
      if (parts.length >= 11) {
        const nBonds = parseInt(parts[10], 10);
        if (!isNaN(nBonds)) {
          bondCount += nBonds;
        }
      }
    } else if (parts[0] === 'forcefield') {
      properties['Forcefield'] = parts.slice(1).join(' ');
    }
  }

  // Each bond is counted twice (once per atom)
  bondCount = Math.floor(bondCount / 2);

  return [{
    name,
    data,
    format: 'hin',
    properties,
    atomCount: atomCount > 0 ? atomCount : undefined,
    bondCount: bondCount > 0 ? bondCount : undefined,
  }];
}

/**
 * Parse CRD file (CHARMM coordinate format - single structure)
 * Basic parser for CHARMM CRD format
 */
function parseCRD(data: string, sourceFilename: string): Omit<MoleculeEntry, 'id'>[] {
  const lines = data.trim().split('\n');
  let name = sourceFilename.replace(/\.crd$/i, '');
  let atomCount = 0;

  const properties: Record<string, string> = {};
  let isExtended = false;

  // Check if this is extended format
  for (const line of lines) {
    if (line.includes('EXT')) {
      isExtended = true;
      break;
    }
  }
  properties['Format'] = isExtended ? 'Extended CRD' : 'Standard CRD';

  // Find atom count line and title
  let foundAtomCount = false;
  for (const line of lines) {
    const trimmed = line.trim();

    // Skip comments (lines starting with *)
    if (trimmed.startsWith('*')) {
      // Extract title from comment
      const titleText = trimmed.substring(1).trim();
      if (titleText && !name.includes(titleText)) {
        if (titleText.toLowerCase() !== 'charmm' && !titleText.startsWith('DATE')) {
          name = titleText;
        }
      }
      continue;
    }

    // First non-comment line should be atom count
    if (!foundAtomCount) {
      const parts = trimmed.split(/\s+/);
      const count = parseInt(parts[0], 10);
      if (!isNaN(count)) {
        atomCount = count;
        foundAtomCount = true;
      }
    }
  }

  return [{
    name,
    data,
    format: 'crd',
    properties,
    atomCount: atomCount > 0 ? atomCount : undefined,
  }];
}

/**
 * Load and parse a file
 */
export function loadFile(file: File): Promise<Omit<MoleculeEntry, 'id'>[]> {
  return new Promise((resolve, reject) => {
    const format = getFormatFromFilename(file.name);

    if (!format) {
      reject(new Error(`Unsupported file format: ${file.name}`));
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      const data = e.target?.result as string;

      if (!data || data.trim().length === 0) {
        reject(new Error('File is empty'));
        return;
      }

      try {
        let molecules: Omit<MoleculeEntry, 'id'>[];

        switch (format) {
          case 'sdf':
            molecules = parseSDFMultiple(data, file.name);
            break;
          case 'mol':
            molecules = parseMOL(data, file.name);
            break;
          case 'mol2':
            molecules = parseMOL2Multiple(data, file.name);
            break;
          case 'pdb':
            molecules = parsePDB(data, file.name);
            break;
          case 'xyz':
            molecules = parseXYZ(data, file.name);
            break;
          case 'hin':
            molecules = parseHIN(data, file.name);
            break;
          case 'crd':
            molecules = parseCRD(data, file.name);
            break;
          default:
            reject(new Error(`Parser not implemented for format: ${format}`));
            return;
        }

        if (molecules.length === 0) {
          reject(new Error('No molecules found in file'));
          return;
        }

        resolve(molecules);
      } catch (err) {
        reject(new Error(`Failed to parse file: ${(err as Error).message}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsText(file);
  });
}

/**
 * Get list of supported file extensions for file input accept attribute
 */
export function getSupportedExtensions(): string {
  return '.sdf,.sd,.mol,.mol2,.pdb,.ent,.xyz,.hin,.crd';
}

/**
 * Parse molecule data from a string with known format
 */
export function parseFromString(
  data: string,
  format: MoleculeFormat,
  sourceName: string = 'molecule'
): Omit<MoleculeEntry, 'id'>[] {
  if (!data || data.trim().length === 0) {
    throw new Error('Data is empty');
  }

  switch (format) {
    case 'sdf':
      return parseSDFMultiple(data, sourceName);
    case 'mol':
      return parseMOL(data, sourceName);
    case 'mol2':
      return parseMOL2Multiple(data, sourceName);
    case 'pdb':
      return parsePDB(data, sourceName);
    case 'xyz':
      return parseXYZ(data, sourceName);
    case 'hin':
      return parseHIN(data, sourceName);
    case 'crd':
      return parseCRD(data, sourceName);
    default:
      throw new Error(`Parser not implemented for format: ${format}`);
  }
}

/**
 * Detect format from file content (when extension is ambiguous)
 */
export function detectFormat(data: string): MoleculeFormat | null {
  const lines = data.trim().split('\n');

  // Check for PDB markers
  if (lines.some(l => l.startsWith('ATOM') || l.startsWith('HETATM') || l.startsWith('HEADER'))) {
    return 'pdb';
  }

  // Check for MOL2 markers
  if (data.includes('@<TRIPOS>MOLECULE')) {
    return 'mol2';
  }

  // Check for SDF delimiter
  if (data.includes('$$$$')) {
    return 'sdf';
  }

  // Check for HIN markers
  if (lines.some(l => l.trim().startsWith('mol ') || l.trim().startsWith('atom '))) {
    return 'hin';
  }

  // Check for CRD format (starts with * comments)
  if (lines[0]?.trim().startsWith('*')) {
    return 'crd';
  }

  // Check for XYZ (first line is atom count)
  if (lines.length >= 2) {
    const firstLine = lines[0].trim();
    if (/^\d+$/.test(firstLine)) {
      return 'xyz';
    }
  }

  // Check for MOL format (has V2000 or V3000)
  if (data.includes('V2000') || data.includes('V3000')) {
    return 'mol';
  }

  return null;
}

/**
 * Count molecules in SDF data without fully parsing
 */
export function countMoleculesInSDF(data: string): number {
  return (data.match(/\$\$\$\$/g) || []).length;
}

/**
 * Extract specific range of molecules from SDF data
 */
export function extractSDFRange(
  data: string,
  start: number,
  end: number,
  sourceName: string = 'molecule'
): Omit<MoleculeEntry, 'id'>[] {
  const blocks = data.split('$$$$').filter(block => block.trim().length > 0);
  const selectedBlocks = blocks.slice(start, end);

  const molecules: Omit<MoleculeEntry, 'id'>[] = [];

  selectedBlocks.forEach((block, index) => {
    const lines = block.trim().split('\n');
    let name = lines[0]?.trim() || `${sourceName}_${start + index + 1}`;

    const properties: Record<string, string> = {};
    let currentPropName = '';
    let inProperty = false;

    for (const line of lines) {
      if (line.startsWith('> <') || line.startsWith('>  <')) {
        const match = line.match(/>\s*<([^>]+)>/);
        if (match) {
          currentPropName = match[1];
          inProperty = true;
        }
      } else if (inProperty && line.trim() && !line.startsWith('>')) {
        properties[currentPropName] = line.trim();
        inProperty = false;
      } else if (line.trim() === '' && inProperty) {
        inProperty = false;
      }
    }

    let atomCount: number | undefined;
    let bondCount: number | undefined;
    if (lines.length > 3) {
      const countsLine = lines[3];
      const counts = countsLine.trim().split(/\s+/);
      if (counts.length >= 2) {
        atomCount = parseInt(counts[0], 10);
        bondCount = parseInt(counts[1], 10);
      }
    }

    molecules.push({
      name,
      data: block.trim() + '\n$$$$',
      format: 'sdf',
      properties,
      atomCount: isNaN(atomCount!) ? undefined : atomCount,
      bondCount: isNaN(bondCount!) ? undefined : bondCount,
    });
  });

  return molecules;
}
